import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { Users, Plus, Trash2, Shield } from "lucide-react";

function ProjectMembers({ projectId, currentUserId, projectOwnerId }) {
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState("personnel");
  const [currentUserRole, setCurrentUserRole] = useState(null);

  useEffect(() => {
    fetchMembers();
    fetchUsers();
  }, [projectId]);

  const fetchMembers = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/members`);
      setMembers(response.data.members);
      
      // Find current user's role
      const currentMember = response.data.members.find(m => m.user_id === currentUserId);
      setCurrentUserRole(currentMember?.role || null);
    } catch (error) {
      toast.error("Üyeler yüklenemedi");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/users");
      setUsers(response.data);
    } catch (error) {
      toast.error("Kullanıcılar yüklenemedi");
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      toast.error("Lütfen bir kullanıcı seçin");
      return;
    }

    try {
      await axiosInstance.post(`/projects/${projectId}/members`, {
        user_id: selectedUserId,
        role: selectedRole
      });
      toast.success("Üye eklendi");
      setDialogOpen(false);
      setSelectedUserId("");
      setSelectedRole("personnel");
      fetchMembers();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Üye ekleme yetkiniz yok");
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Üye eklenemedi");
      } else {
        toast.error("Üye eklenemedi");
      }
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm("Bu üyeyi projeden çıkarmak istediğinizden emin misiniz?")) return;

    try {
      await axiosInstance.delete(`/projects/${projectId}/members/${userId}`);
      toast.success("Üye çıkarıldı");
      fetchMembers();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Üye çıkarma yetkiniz yok. Sadece admin yapabilir.");
      } else {
        toast.error("Üye çıkarılamadı");
      }
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axiosInstance.put(`/projects/${projectId}/members/role`, {
        user_id: userId,
        new_role: newRole
      });
      toast.success("Rol güncellendi");
      fetchMembers();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Rol değiştirme yetkiniz yok. Sadece admin yapabilir.");
      } else {
        toast.error("Rol güncellenemedi");
      }
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "manager":
        return "bg-blue-100 text-blue-800";
      case "engineer":
        return "bg-purple-100 text-purple-800";
      case "personnel":
      case "member":  // Backward compatibility
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Admin";
      case "manager":
        return "Müdür";
      case "engineer":
        return "Mühendis";
      case "personnel":
        return "Personel";
      case "member":  // Backward compatibility
        return "Personel";
      default:
        return role;
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const canManageMembers = currentUserRole === "admin" || currentUserRole === "manager";
  const isAdmin = currentUserRole === "admin";

  const availableUsers = users.filter(
    u => !members.some(m => m.user_id === u.id)
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <Users className="h-5 w-5" />
          Proje Üyeleri ({members.length})
        </h3>
        {canManageMembers && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="add-member-button">
                <Plus className="h-4 w-4" />
                Üye Ekle
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Projeye Üye Ekle</DialogTitle>
                <DialogDescription>
                  Kullanıcı seçin ve rol atayın.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Kullanıcı</label>
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger data-testid="select-user">
                      <SelectValue placeholder="Kullanıcı seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Rol</label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger data-testid="select-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personnel">Personel</SelectItem>
                      <SelectItem value="engineer">Mühendis</SelectItem>
                      <SelectItem value="manager">Müdür</SelectItem>
                      {isAdmin && <SelectItem value="admin">Admin</SelectItem>}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {selectedRole === "admin" && "Tüm yetkiler (proje silme, üye yönetimi, rol değiştirme)"}
                    {selectedRole === "manager" && "Üye ekleme ve tüm görevleri yönetme yetkisi"}
                    {selectedRole === "engineer" && "Atanan görevlerde düzenleme, görüntüleme yetkisi"}
                    {selectedRole === "personnel" && "Sadece atanan görevlerde durum ve zaman değiştirme"}
                  </p>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-add-member">
                  Ekle
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {members.map((member) => (
          <div
            key={member.user_id}
            className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
            data-testid={`member-${member.user_id}`}
          >
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-blue-100 text-blue-700">
                  {getInitials(member.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="font-medium">{member.name}</div>
                <div className="text-sm text-gray-500">{member.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {member.user_id === projectOwnerId ? (
                <Badge className={getRoleBadgeColor("admin")}>
                  <Shield className="h-3 w-3 mr-1" />
                  Proje Sahibi
                </Badge>
              ) : (
                <>
                  {isAdmin ? (
                    <Select
                      value={member.role}
                      onValueChange={(newRole) => handleChangeRole(member.user_id, newRole)}
                    >
                      <SelectTrigger className="w-32" data-testid={`role-select-${member.user_id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Üye</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={getRoleBadgeColor(member.role)}>
                      {getRoleLabel(member.role)}
                    </Badge>
                  )}
                  {isAdmin && member.user_id !== currentUserId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveMember(member.user_id)}
                      data-testid={`remove-member-${member.user_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {members.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          Henüz üye eklenmemiş
        </div>
      )}
    </div>
  );
}

export default ProjectMembers;
